import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Checkbox,
  FormControlLabel,
  Grid,
  Typography,
  MenuItem,
  CircularProgress,
  Stack,
  IconButton,
  Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import useCustomerRequest from '../../../requests/CustomerRequest';
import useComplianceRequest from '../../../requests/ComplianceRequest';
import CountrySelect from '../../common/inputs/CountrySelect';

export default function RequestCampaignForm({
  showRequestCampaignModal,
  hideModal,
  errorHandler,
  refreshCustomer,
  customerId,
  customerName,
  customerAccountNo,
}) {
  const [formData, setFormData] = useState({
    // Brand Information defaults
    autoRenewal: false,

    // Campaign Information defaults
    embeddedLink: false,
    embeddedPhone: false,
    numberPool: false,
    ageGated: false,
    directLending: false,
    subscriberOptIn: false,
    subscriberOptOut: false,
    subscriberHelp: false,
    campaignAutoRenewal: false,
    companyName: customerName,
    brandReferenceId: customerAccountNo,
    campaignReferenceId: customerAccountNo + ' - Campaign',
    country: 'US',
    ein_issuing_country: 'US',
  });
  const [subUsecases, setSubUsecases] = useState({});
  const [validated, setValidated] = useState(false);
  const { requestBrandAndCampaign } = useCustomerRequest();
  const { getOptions } = useComplianceRequest();
  const [errorText, setErrorText] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const [enums, setEnums] = useState(null);

  useEffect(() => {
    const loadEnums = async () => {
      const enums = await getOptions(
        [
          'usecase',
          'vertical',
          'entityType',
          'stockExchange',
          'brandRelationship',
        ],
        errorHandler
      );
      console.log('enums:', enums);
      setEnums(enums);
    };

    loadEnums();
  }, [customerId]);

  const handleFormChange = (event) => {
    const value =
      event.target.type === 'checkbox'
        ? event.target.checked
        : event.target.value;

    setFormData({
      ...formData,
      [event.target.name]: value,
    });
  };

  const handleSubUsecaseFormChange = (event) => {
    let target = event.target;
    let value = target.type === 'checkbox' ? target.checked : target.value;
    let name = target.name;

    setSubUsecases({
      ...subUsecases,
      [name]: value,
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const form = document.getElementById('requestCampaignForm');

    setValidated(true);
    if (form.checkValidity() === false) {
      event.stopPropagation();
      console.log('Form is invalid');
      return;
    }

    setIsLoading(true);

    console.log(formData);
    const body = {
      customer_id: customerId,
      Brand: {
        brand_relationship: formData.brandRelationship,
        business_contact_email: formData.businessContactEmail,
        city: formData.city,
        company_name: formData.companyName,
        country: formData.country,
        display_name: formData.displayName,
        ein: formData.ein,
        ein_issuing_country: formData.einIssuingCountry,
        email: formData.email,
        entity_type: formData.entityType,
        first_name: formData.firstName,
        ip_address: formData.ipAddress,
        last_name: formData.lastName,
        phone: formData.phone,
        postal_code: formData.postalCode,
        reference_id: formData.brandReferenceId,
        state: formData.state,
        stock_exchange: formData.stockExchange,
        stock_symbol: formData.stockSymbol,
        street: formData.streetAddress,
        vertical: formData.vertical,
        website: formData.website,
      },
      Campaign: {
        affiliate_marketing: formData.affiliateMarketing, // This field is not in the form yet?
        age_gated: formData.ageGated,
        auto_renewal: formData.campaignAutoRenewal,
        description: formData.description,
        direct_lending: formData.directLending,
        embedded_link: formData.embeddedLink,
        embedded_phone: formData.embeddedPhone,
        help_message: formData.helpMessage,
        message_flow: formData.messageFlow,
        number_pool: formData.numberPool,
        reference_id: formData.campaignReferenceId,
        sample_1: formData.sampleMessage1,
        sample_2: formData.sampleMessage2,
        sample_3: formData.sampleMessage3,
        sample_4: formData.sampleMessage4,
        sample_5: formData.sampleMessage5,
        subscriber_help: formData.subscriberHelp,
        subscriber_opt_in: formData.subscriberOptIn,
        subscriber_opt_out: formData.subscriberOptOut,
        use_case: formData.useCase,
        vertical: formData.campaignVertical,
        sub_use_cases: subUsecases,
      },
    };
    const response = await requestBrandAndCampaign(body, setErrorText);
    if (response.status === 200) {
      hideModal();
      refreshCustomer();
    }
    setIsLoading(false);
  };

  const generateCheckbox = (label, name, isSubUsecase) => {
    return (
      <FormControlLabel
        control={
          <Checkbox
            name={name}
            checked={isSubUsecase ? subUsecases[name] : formData[name]}
            onChange={
              isSubUsecase ? handleSubUsecaseFormChange : handleFormChange
            }
          />
        }
        label={label}
      />
    );
  };

  const generateSubUsecaseList = () => {
    if (!enums || !enums?.usecase) {
      return null;
    }

    const usecases = Object.keys(enums?.usecase)
      ?.filter((el) => enums?.usecase[el]?.validSubUsecase === true)
      .map((item) => generateCheckbox(item, item, true));

    return usecases;
  };

  const getEntityTypeRequired = (fieldName) => {
    if (!formData.entityType) {
      return false;
    }

    const fields = {
      displayName: {
        SOLE_PROPRIETOR: true,
        PRIVATE_PROFIT: true,
        PUBLIC_PROFIT: true,
        NON_PROFIT: true,
        GOVERNMENT: true,
      },
      companyName: {
        SOLE_PROPRIETOR: false,
        PRIVATE_PROFIT: true,
        PUBLIC_PROFIT: true,
        NON_PROFIT: true,
        GOVERNMENT: true,
      },
      ein: {
        SOLE_PROPRIETOR: false,
        PRIVATE_PROFIT: true,
        PUBLIC_PROFIT: true,
        NON_PROFIT: true,
        GOVERNMENT: true,
      },
      einIssuingCountry: {
        SOLE_PROPRIETOR: false,
        PRIVATE_PROFIT: false,
        PUBLIC_PROFIT: false,
        NON_PROFIT: false,
        GOVERNMENT: false,
      },
      altBusinessId: {
        SOLE_PROPRIETOR: false,
        PRIVATE_PROFIT: false,
        PUBLIC_PROFIT: false,
        NON_PROFIT: false,
        GOVERNMENT: false,
      },
      altBusinessIdType: {
        SOLE_PROPRIETOR: false,
        PRIVATE_PROFIT: false,
        PUBLIC_PROFIT: false,
        NON_PROFIT: false,
        GOVERNMENT: false,
      },
      firstName: {
        SOLE_PROPRIETOR: true,
        PRIVATE_PROFIT: false,
        PUBLIC_PROFIT: false,
        NON_PROFIT: false,
        GOVERNMENT: false,
      },
      lastName: {
        SOLE_PROPRIETOR: true,
        PRIVATE_PROFIT: false,
        PUBLIC_PROFIT: false,
        NON_PROFIT: false,
        GOVERNMENT: false,
      },
      phone: {
        SOLE_PROPRIETOR: true,
        PRIVATE_PROFIT: true,
        PUBLIC_PROFIT: true,
        NON_PROFIT: true,
        GOVERNMENT: true,
      },
      mobilePhone: {
        SOLE_PROPRIETOR: true,
        PRIVATE_PROFIT: false,
        PUBLIC_PROFIT: false,
        NON_PROFIT: false,
        GOVERNMENT: false,
      },
      streetAddress: {
        SOLE_PROPRIETOR: true,
        PRIVATE_PROFIT: true,
        PUBLIC_PROFIT: true,
        NON_PROFIT: true,
        GOVERNMENT: true,
      },
      city: {
        SOLE_PROPRIETOR: true,
        PRIVATE_PROFIT: true,
        PUBLIC_PROFIT: true,
        NON_PROFIT: true,
        GOVERNMENT: true,
      },
      state: {
        SOLE_PROPRIETOR: true,
        PRIVATE_PROFIT: true,
        PUBLIC_PROFIT: true,
        NON_PROFIT: true,
        GOVERNMENT: true,
      },
      postalCode: {
        SOLE_PROPRIETOR: true,
        PRIVATE_PROFIT: true,
        PUBLIC_PROFIT: true,
        NON_PROFIT: true,
        GOVERNMENT: true,
      },
      country: {
        SOLE_PROPRIETOR: true,
        PRIVATE_PROFIT: true,
        PUBLIC_PROFIT: true,
        NON_PROFIT: true,
        GOVERNMENT: true,
      },
      email: {
        SOLE_PROPRIETOR: true,
        PRIVATE_PROFIT: true,
        PUBLIC_PROFIT: true,
        NON_PROFIT: true,
        GOVERNMENT: true,
      },
      stockExchange: {
        SOLE_PROPRIETOR: false,
        PRIVATE_PROFIT: false,
        PUBLIC_PROFIT: true,
        NON_PROFIT: false,
        GOVERNMENT: false,
      },
      stockSymbol: {
        SOLE_PROPRIETOR: false,
        PRIVATE_PROFIT: false,
        PUBLIC_PROFIT: true,
        NON_PROFIT: false,
        GOVERNMENT: false,
      },
      website: {
        SOLE_PROPRIETOR: false,
        PRIVATE_PROFIT: false,
        PUBLIC_PROFIT: false,
        NON_PROFIT: false,
        GOVERNMENT: false,
      },
      brandRelationship: {
        SOLE_PROPRIETOR: false,
        PRIVATE_PROFIT: true,
        PUBLIC_PROFIT: true,
        NON_PROFIT: true,
        GOVERNMENT: true,
      },
      vertical: {
        SOLE_PROPRIETOR: false,
        PRIVATE_PROFIT: true,
        PUBLIC_PROFIT: true,
        NON_PROFIT: true,
        GOVERNMENT: true,
      },
      // This is actually only needed for sole proprietors but we've enforced it.
      referenceId: {
        SOLE_PROPRIETOR: true,
        PRIVATE_PROFIT: true,
        PUBLIC_PROFIT: true,
        NON_PROFIT: true,
        GOVERNMENT: true,
      },
      tag: {
        SOLE_PROPRIETOR: false,
        PRIVATE_PROFIT: false,
        PUBLIC_PROFIT: false,
        NON_PROFIT: false,
        GOVERNMENT: false,
      },
      businessContactEmail: {
        SOLE_PROPRIETOR: false,
        PRIVATE_PROFIT: false,
        PUBLIC_PROFIT: true,
        NON_PROFIT: false,
        GOVERNMENT: false,
      },
    };

    if (!fields[fieldName]) {
      return false;
    }

    return fields[fieldName][formData.entityType];
  };

  const generateTextField = (label, name, helperText) => {
    const isRequired = getEntityTypeRequired(name);
    const hasError = isRequired && validated && !formData[name];

    return (
      <Grid sx={{ maxWidth: { md: 460 } }} size={{ xs: 12, lg: 6 }}>
        <FormControl required={isRequired} fullWidth>
          <TextField
            type="text"
            name={name}
            label={label}
            required={isRequired}
            value={formData[name]}
            onChange={handleFormChange}
            error={hasError}
            helperText={hasError ? helperText : ''}
          />
        </FormControl>
      </Grid>
    );
  };

  const generateDropdown = (
    enumVariable,
    label,
    fieldName,
    helperText,
    isArray
  ) => {
    if (!enums || !enums?.[enumVariable]) {
      return null;
    }

    const isRequired = getEntityTypeRequired(fieldName);
    const hasError = isRequired && validated && !formData[fieldName];

    const menuItems = isArray
      ? enums?.[enumVariable]?.map((item, index) => (
          <MenuItem key={index} value={item}>
            {item}
          </MenuItem>
        ))
      : Object.keys(enums?.[enumVariable])?.map((item, index) => (
          <MenuItem key={index} value={item}>
            {item}
          </MenuItem>
        ));

    return (
      <Grid sx={{ maxWidth: { md: 460 } }} size={{ xs: 12, lg: 6 }}>
        <FormControl required={isRequired} fullWidth>
          <TextField
            select
            label={label}
            name={fieldName}
            required={isRequired}
            value={formData?.[fieldName] || ''}
            onChange={handleFormChange}
            size="medium"
            displayEmpty
            error={hasError}
            helperText={hasError ? helperText : ''}
          >
            {menuItems}
          </TextField>
        </FormControl>
      </Grid>
    );
  };

  if (!enums) {
    return (
      <Box
        sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Dialog
      open={showRequestCampaignModal}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      fullWidth
      maxWidth="xl"
      disableEscapeKeyDown
    >
      <DialogTitle
        id="alert-dialog-title"
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        Request Campaign
        <IconButton
          aria-label="close"
          onClick={hideModal}
          sx={{
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box
          component="form"
          noValidate
          validated={validated}
          onChange={handleFormChange}
          onSubmit={handleSubmit}
          id="requestCampaignForm"
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '1rem',
            }}
          >
            <Box>
              <Typography variant="h2">Brand Information</Typography>
              <Grid container spacing={2} sx={{ mt: 2 }}>
                {generateDropdown(
                  'entityType',
                  'Entity Type',
                  'entityType',
                  'Please provide an entity type.',
                  true
                )}
                {generateTextField(
                  'Business Contact Email',
                  'businessContactEmail',
                  'Please provide a valid email address.'
                )}
                {generateTextField(
                  'First Name',
                  'firstName',
                  'Please provide your first name.'
                )}
                {generateTextField(
                  'Last Name',
                  'lastName',
                  'Please provide your last name.'
                )}
                {generateTextField(
                  'Display Name',
                  'displayName',
                  'Please provide a display name.'
                )}
                {generateTextField(
                  'Company Name',
                  'companyName',
                  'Please provide a company name.'
                )}
                {generateTextField('EIN', 'ein', 'Please provide an EIN.')}
                <Grid sx={{ maxWidth: { md: 460 } }} size={{ xs: 12, lg: 6 }}>
                  <CountrySelect
                    name="einIssuingCountry"
                    label="EIN Issuing Country"
                    size="medium"
                    fullWidth
                    selectDefault
                    value={formData['einIssuingCountry']}
                    onChange={(_, newValue) =>
                      handleFormChange({
                        target: {
                          name: 'einIssuingCountry',
                          value: newValue,
                        },
                      })
                    }
                    returnIso2
                  />
                </Grid>
                {generateTextField(
                  'Phone',
                  'phone',
                  'Please provide a phone number.'
                )}
                {generateTextField(
                  'Street Address',
                  'streetAddress',
                  'Please provide a street address.'
                )}
                {generateTextField('City', 'city', 'Please provide a city.')}
                {generateTextField('State', 'state', 'Please provide a state.')}
                {generateTextField(
                  'Postal Code',
                  'postalCode',
                  'Please provide a postal code.'
                )}
                <Grid sx={{ maxWidth: { md: 460 } }} size={{ xs: 12, lg: 6 }}>
                  <CountrySelect
                    name="country"
                    label="Country"
                    size="medium"
                    required
                    fullWidth
                    selectDefault
                    value={formData['country']}
                    onChange={(_, newValue) =>
                      handleFormChange({
                        target: {
                          name: 'country',
                          value: newValue,
                        },
                      })
                    }
                    returnIso2
                    error={validated && !formData['country']}
                    helperText={
                      validated && !formData['country']
                        ? 'Please provide a country.'
                        : ''
                    }
                  />
                </Grid>
                {generateTextField(
                  'Email',
                  'email',
                  'Please provide an email address.'
                )}
                {generateDropdown(
                  'stockExchange',
                  'Stock Exchange',
                  'stockExchange',
                  'Please provide a stock exchange.',
                  true
                )}
                {generateTextField(
                  'Stock Symbol',
                  'stockSymbol',
                  'Please provide a stock symbol.'
                )}
                {generateTextField(
                  'IP Address',
                  'ipAddress',
                  'Please provide an IP address.'
                )}
                {generateTextField(
                  'Website',
                  'website',
                  'Please provide a website.'
                )}
                {generateDropdown(
                  'brandRelationship',
                  'Brand Relationship',
                  'brandRelationship',
                  'Please provide a brand relationship.',
                  true
                )}
                {generateDropdown(
                  'vertical',
                  'Vertical',
                  'vertical',
                  'Please provide a vertical.'
                )}
                {generateTextField('Alt Business ID', 'altBusinessId', false)}
                {generateTextField(
                  'Alt Business ID Type',
                  'altBusinessIdType',
                  false
                )}
                {generateTextField(
                  'Reference ID',
                  'brandReferenceId',
                  'Please provide a reference ID.'
                )}

                <Grid size={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="autoRenewal"
                        checked={formData.autoRenewal}
                        onChange={handleFormChange}
                      />
                    }
                    label="Auto Renewal"
                  />
                </Grid>
              </Grid>
            </Box>

            <Box>
              <Typography variant="h2">Campaign Information</Typography>
              <Grid container spacing={2} sx={{ mt: 2 }}>
                {generateDropdown(
                  'vertical',
                  'Vertical',
                  'campaignVertical',
                  'Please provide a vertical.'
                )}
                {generateDropdown(
                  'usecase',
                  'Use Case',
                  'useCase',
                  'Please provide a use case.'
                )}
                {generateTextField('Reseller ID', 'resellerId')}
                <Grid size={4}>
                  <FormControl fullWidth>
                    {generateCheckbox('Embedded Link', 'embeddedLink')}
                    {generateCheckbox('Embedded Phone', 'embeddedPhone')}
                    {generateCheckbox('Number Pool', 'numberPool')}
                    {generateCheckbox('Age Gated', 'ageGated')}
                    {generateCheckbox('Direct Lending', 'directLending')}
                    {generateCheckbox('Subscriber Opt-in', 'subscriberOptIn')}
                    {generateCheckbox('Subscriber Opt-out', 'subscriberOptOut')}
                    {generateCheckbox('Subscriber Help', 'subscriberHelp')}
                  </FormControl>
                </Grid>
                <Grid size={4}>
                  <FormControl fullWidth>
                    {generateSubUsecaseList()}
                  </FormControl>
                </Grid>
                <Grid size={12}>
                  <FormControl required fullWidth>
                    <TextField
                      label="Description"
                      name="description"
                      required
                      multiline
                      rows={4}
                      onChange={handleFormChange}
                      error={validated && !formData.description}
                      helperText={
                        validated && !formData.description
                          ? 'Please provide a description.'
                          : ''
                      }
                    />
                  </FormControl>
                </Grid>
                <Grid size={12}>
                  <FormControl required fullWidth>
                    <TextField
                      label="Sample Message 1"
                      name="sampleMessage1"
                      required
                      multiline
                      rows={4}
                      onChange={handleFormChange}
                      error={validated && !formData.sampleMessage1}
                      helperText={
                        validated && !formData.sampleMessage1
                          ? 'Please provide sample message 1.'
                          : ''
                      }
                    />
                  </FormControl>
                </Grid>
                <Grid size={12}>
                  <FormControl fullWidth>
                    <TextField
                      label="Sample Message 2"
                      name="sampleMessage2"
                      multiline
                      rows={4}
                      onChange={handleFormChange}
                    />
                  </FormControl>
                </Grid>
                <Grid size={12}>
                  <FormControl fullWidth>
                    <TextField
                      label="Sample Message 3"
                      name="sampleMessage3"
                      multiline
                      rows={4}
                      onChange={handleFormChange}
                    />
                  </FormControl>
                </Grid>
                <Grid size={12}>
                  <FormControl fullWidth>
                    <TextField
                      label="Sample Message 4"
                      name="sampleMessage4"
                      multiline
                      rows={4}
                      onChange={handleFormChange}
                    />
                  </FormControl>
                </Grid>
                <Grid size={12}>
                  <FormControl fullWidth>
                    <TextField
                      label="Sample Message 5"
                      name="sampleMessage5"
                      multiline
                      rows={4}
                      onChange={handleFormChange}
                    />
                  </FormControl>
                </Grid>
                <Grid size={12}>
                  <FormControl required fullWidth>
                    <TextField
                      label="Message Flow"
                      name="messageFlow"
                      required
                      multiline
                      rows={4}
                      onChange={handleFormChange}
                      error={validated && !formData.messageFlow}
                      helperText={
                        validated && !formData.messageFlow
                          ? 'Please provide a message flow.'
                          : ''
                      }
                    />
                  </FormControl>
                </Grid>
                <Grid size={12}>
                  <FormControl required fullWidth>
                    <TextField
                      label="Help Message"
                      name="helpMessage"
                      required
                      multiline
                      rows={4}
                      onChange={handleFormChange}
                      error={validated && !formData.helpMessage}
                      helperText={
                        validated && !formData.helpMessage
                          ? 'Please provide a help message.'
                          : ''
                      }
                    />
                  </FormControl>
                </Grid>
                <Grid size={12}>
                  <FormControl required fullWidth>
                    <TextField
                      type="text"
                      name="campaignReferenceId"
                      label="Reference ID"
                      required
                      value={formData.campaignReferenceId}
                      onChange={handleFormChange}
                      error={validated && !formData.campaignReferenceId}
                      helperText={
                        validated && !formData.campaignReferenceId
                          ? 'Please provide a reference ID.'
                          : ''
                      }
                    />
                  </FormControl>
                </Grid>
                <Grid size={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="campaignAutoRenewal"
                        checked={formData.campaignAutoRenewal}
                        onChange={handleFormChange}
                      />
                    }
                    label="Auto Renewal"
                  />
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ display: 'flex', justifyContent: 'space-between' }}>
        {(errorText && <Alert severity="error">{errorText}</Alert>) || (
          <Stack />
        )}
        <Stack direction="row" spacing={2} alignItems="center">
          <Button onClick={hideModal} variant="contained" color="secondary">
            Cancel Request
          </Button>
          <Button
            type="submit"
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            disabled={isLoading}
          >
            {isLoading ? <CircularProgress size={24} /> : 'Submit Campaign'}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
